import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

import { ApplyDialog } from './ApplyDialog';

/** Card-footer scale: a compact control with its icon leading. */
const COMPACT = 'gap-1.5 px-3 text-[12px]';

/**
 * Apply from wherever an internship is listed -- the same rules, and the
 * same dialog, as the listing's own page.
 *
 *   student, open, not applied  -> "Apply now", opens ApplyDialog
 *   student, already applied    -> "Applied", disabled
 *   student, closed             -> "Closed", disabled
 *   signed out                  -> "Apply now", sends them to sign in and
 *                                  brings them back to this list afterwards
 *   company or admin            -> nothing: they cannot apply
 *
 * `internship` needs `id`, `title`, `company`, `is_open` and `has_applied`,
 * which the search results carry. The dialog is mounted only while open, so
 * a page of twenty cards does not hold twenty dialogs.
 */
export function ApplyButton({ internship, size = 'sm', className }) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (isAuthenticated && user?.role !== 'STUDENT') return null;

  if (!isAuthenticated) {
    return (
      <Button
        size={size}
        className={cn(COMPACT, className)}
        onClick={() => navigate(paths.login, { state: { from: location } })}
        title="Sign in to apply"
      >
        <Icon name="send" size={13} />
        Apply now
      </Button>
    );
  }

  if (internship.has_applied) {
    return (
      <Button size={size} variant="tintPositive" className={cn(COMPACT, className)} disabled>
        <Icon name="check" size={13} />
        Applied
      </Button>
    );
  }

  if (!internship.is_open) {
    return (
      <Button size={size} variant="secondary" className={cn(COMPACT, className)} disabled>
        Closed
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        className={cn(COMPACT, className)}
        onClick={() => setOpen(true)}
        title={`Apply to ${internship.title}`}
      >
        <Icon name="send" size={13} />
        Apply now
      </Button>
      {open && <ApplyDialog internship={internship} open onClose={() => setOpen(false)} />}
    </>
  );
}
